"use client";

import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PaymentMethodSelector } from "@/components/payment-methods/payment-method-selector";
import { TrustPilotSection } from "@/components/manage-workspace-modal/sections/trustpilot-section";
import { QuotesDomainSection } from "@/components/manage-workspace-modal/sections/quotes-domain-section";
import { EmailConfigSection } from "@/components/manage-workspace-modal/sections/email-config-section";
import { BookingEmailConfigSection } from "@/components/manage-workspace-modal/sections/booking-email-config-section";
import { useBrandById } from "@/hooks/use-brands";
import { SDK } from "@/@sdk";
import { selectIntegrationConfig_Stripe } from "@/store/modules/workspace/workspace.selectors";
import { toast } from "sonner";

function WizardSkipActions({
  onSkip,
  onContinue,
  continueLabel = "Continue",
}: {
  onSkip: () => void;
  onContinue: () => void;
  continueLabel?: string;
}) {
  return (
    <div className="flex justify-end gap-2 border-t pt-4">
      <Button type="button" variant="outline" onClick={onSkip}>
        Skip for now
      </Button>
      <Button type="button" onClick={onContinue}>
        {continueLabel}
      </Button>
    </div>
  );
}

export function BrandTrustPilotStep({
  brandId,
  onContinue,
  onSkip,
}: {
  brandId: string;
  onContinue: () => void;
  onSkip: () => void;
}) {
  const [wantsTrustPilot, setWantsTrustPilot] = useState<boolean | null>(null);

  if (wantsTrustPilot === null) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Do you display TrustPilot reviews on customer quotes? You can configure
          this later from Manage Workspace.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => setWantsTrustPilot(true)}>
            Yes, set up TrustPilot
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onSkip}
          >
            No, skip for now
          </Button>
        </div>
      </div>
    );
  }

  if (!wantsTrustPilot) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
        Find your Business Unit ID and Template ID in{" "}
        <a
          href="https://business.trustpilot.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
        >
          TrustPilot Business
          <ExternalLink className="h-3.5 w-3.5" />
        </a>{" "}
        → Integrations → Website widgets.
      </div>
      <div className="max-h-[45vh] overflow-y-auto pr-1">
        <TrustPilotSection brandId={brandId} />
      </div>
      <WizardSkipActions onSkip={onSkip} onContinue={onContinue} />
    </div>
  );
}

export function BrandDomainsEmailStep({
  brandId,
  onContinue,
  onSkip,
}: {
  brandId: string;
  onContinue: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Custom domains and email delivery can take time to verify with DNS. Set
        up what you can now — skipped items stay on your brand checklist.
      </p>
      <Accordion type="multiple" className="w-full" defaultValue={["quotes-domain"]}>
        <AccordionItem value="quotes-domain">
          <AccordionTrigger>Quotes domain</AccordionTrigger>
          <AccordionContent>
            <div className="max-h-[40vh] overflow-y-auto pr-1">
              <QuotesDomainSection brandId={brandId} />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="quote-email">
          <AccordionTrigger>Quote email delivery</AccordionTrigger>
          <AccordionContent>
            <div className="max-h-[40vh] overflow-y-auto pr-1">
              <EmailConfigSection brandId={brandId} />
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="booking-email">
          <AccordionTrigger>Booking email domain</AccordionTrigger>
          <AccordionContent>
            <div className="max-h-[40vh] overflow-y-auto pr-1">
              <BookingEmailConfigSection brandId={brandId} />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <WizardSkipActions onSkip={onSkip} onContinue={onContinue} />
    </div>
  );
}

export function BrandStripeBrandStep({
  brandId,
  onContinue,
  onSkip,
}: {
  brandId: string;
  onContinue: () => void;
  onSkip: () => void;
}) {
  const brand = useBrandById(brandId);
  const integrationConfig = useSelector(selectIntegrationConfig_Stripe);
  const stripeConnected = Boolean(integrationConfig?.auth?.apiKey);
  const [draftPaymentMethodId, setDraftPaymentMethodId] = useState(
    () => brand?.stripeConfig?.customerPaymentMethodId?.trim() ?? ""
  );
  const [isSaving, setIsSaving] = useState(false);

  const savedPaymentMethodId =
    brand?.stripeConfig?.customerPaymentMethodId?.trim() ?? "";
  const isDirty = draftPaymentMethodId !== savedPaymentMethodId;

  const handleSave = useCallback(async () => {
    if (!brandId) return;
    setIsSaving(true);
    try {
      await SDK.brands.editById(brandId, {
        stripeConfig: {
          ...(brand?.stripeConfig ?? {}),
          customerPaymentMethodId: draftPaymentMethodId.trim() || undefined,
        },
      });
      toast.success("Stripe payment method saved");
    } catch {
      toast.error("Failed to save Stripe payment method");
    } finally {
      setIsSaving(false);
    }
  }, [brand?.stripeConfig, brandId, draftPaymentMethodId]);

  if (!stripeConnected) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Stripe is not connected at the workspace level yet. Connect Stripe in
          Manage Workspace → Integrations to map card payments for this brand.
        </p>
        <div className="flex justify-end border-t pt-4">
          <Button type="button" onClick={onContinue}>
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Choose which customer payment method receives Stripe card payments for
        automatic receipts on this brand.
      </p>
      <div className="space-y-3 rounded-lg border p-4">
        <p className="text-sm font-medium">{brand?.name ?? "This brand"}</p>
        <PaymentMethodSelector
          id={`brand-wizard-stripe-payment-method-${brandId}`}
          variant="customer"
          value={draftPaymentMethodId}
          onValueChange={setDraftPaymentMethodId}
          label="Customer payment method"
          currencyFilter={brand?.homeCurrency}
          brandIdFilter={brandId}
          emptyHint={
            brand?.homeCurrency
              ? `No customer payment methods for ${brand.homeCurrency}`
              : undefined
          }
        />
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isSaving || !isDirty}
            onClick={() => void handleSave()}
          >
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Save
          </Button>
        </div>
      </div>
      <WizardSkipActions onSkip={onSkip} onContinue={onContinue} />
    </div>
  );
}
